import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Sparkles, Copy, X } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GlobalSearch({ doctorId, isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (query.length >= 2) {
        performSearch();
      } else {
        setResults(null);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  const performSearch = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/search/${doctorId}?q=${encodeURIComponent(query)}`);
      setResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (type, id) => {
    onClose();
    setQuery('');
    setResults(null);
    
    switch (type) {
      case 'script':
        navigate(`/library/${doctorId}`);
        break;
      case 'idea':
        navigate(`/ideas/${doctorId}`);
        break;
      case 'template':
        navigate(`/templates/${doctorId}`);
        break;
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'script': return FileText;
      case 'idea': return Sparkles;
      case 'template': return Copy;
      default: return FileText;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white rounded-3xl max-w-2xl p-0 gap-0">
        {/* Search Input */}
        <div className="p-6 border-b border-[#2C5F44]/10">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#5C7063]" strokeWidth={1.5} />
            <Input
              ref={inputRef}
              type="text"
              placeholder="Search scripts, ideas, templates..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-12 pr-12 py-6 rounded-xl border-[#2C5F44]/20 text-lg"
              data-testid="global-search-input"
            />
            {query && (
              <button
                onClick={() => {
                  setQuery('');
                  setResults(null);
                }}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-[#5C7063] hover:text-[#1C2A21]"
              >
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            )}
          </div>
          {query.length > 0 && query.length < 2 && (
            <p className="text-sm text-[#5C7063] mt-2">Type at least 2 characters to search</p>
          )}
        </div>

        {/* Results */}
        <div className="max-h-96 overflow-y-auto p-6">
          {loading && (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-4 border-[#2C5F44] border-t-transparent rounded-full animate-spin mx-auto" />
              <p className="text-sm text-[#5C7063] mt-2">Searching...</p>
            </div>
          )}

          {!loading && results && results.total === 0 && (
            <div className="text-center py-8">
              <Search className="w-12 h-12 text-[#5C7063] mx-auto mb-3 opacity-50" strokeWidth={1.5} />
              <p className="text-[#5C7063]">No results found for "{query}"</p>
            </div>
          )}

          {!loading && results && results.total > 0 && (
            <div className="space-y-6">
              {/* Scripts */}
              {results.scripts.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-[#5C7063] mb-3 uppercase">
                    Scripts ({results.scripts.length})
                  </h3>
                  <div className="space-y-2">
                    {results.scripts.map((script) => (
                      <button
                        key={script.id}
                        onClick={() => handleResultClick('script', script.id)}
                        className="w-full text-left p-3 rounded-xl hover:bg-[#F4F1EA] transition-colors flex items-start gap-3"
                        data-testid={`search-result-script-${script.id}`}
                      >
                        <FileText className="w-5 h-5 text-[#2C5F44] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="flex-1 min-w-0">
                          <p className="text-[#1C2A21] font-medium truncate">{script.topic}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-[#5C7063]">
                              {new Date(script.created_at).toLocaleDateString()}
                            </span>
                            {script.compliance_status && (
                              <Badge className={`text-xs ${
                                script.compliance_status.toLowerCase() === 'low' ? 'bg-green-100 text-green-700' :
                                script.compliance_status.toLowerCase() === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {script.compliance_status}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Ideas */}
              {results.ideas.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-[#5C7063] mb-3 uppercase">
                    Ideas ({results.ideas.length})
                  </h3>
                  <div className="space-y-2">
                    {results.ideas.map((idea) => (
                      <button
                        key={idea.id}
                        onClick={() => handleResultClick('idea', idea.id)}
                        className="w-full text-left p-3 rounded-xl hover:bg-[#F4F1EA] transition-colors flex items-start gap-3"
                        data-testid={`search-result-idea-${idea.id}`}
                      >
                        <Sparkles className="w-5 h-5 text-[#D98A6C] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="flex-1 min-w-0">
                          <p className="text-[#1C2A21] font-medium truncate">{idea.topic}</p>
                          <span className="text-xs text-[#5C7063]">{idea.format}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Templates */}
              {results.templates.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-[#5C7063] mb-3 uppercase">
                    Templates ({results.templates.length})
                  </h3>
                  <div className="space-y-2">
                    {results.templates.map((template) => (
                      <button
                        key={template.id}
                        onClick={() => handleResultClick('template', template.id)}
                        className="w-full text-left p-3 rounded-xl hover:bg-[#F4F1EA] transition-colors flex items-start gap-3"
                        data-testid={`search-result-template-${template.id}`}
                      >
                        <Copy className="w-5 h-5 text-[#2C5F44] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                        <div className="flex-1 min-w-0">
                          <p className="text-[#1C2A21] font-medium truncate">{template.template_name}</p>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-[#5C7063]">{template.format}</span>
                            {template.use_count > 0 && (
                              <span className="text-xs text-[#D98A6C]">• Used {template.use_count}x</span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#2C5F44]/10 bg-[#F4F1EA] rounded-b-3xl">
          <div className="flex items-center justify-between text-xs text-[#5C7063]">
            <span>Press <kbd className="px-2 py-1 bg-white rounded border border-[#2C5F44]/20">ESC</kbd> to close</span>
            <span>Press <kbd className="px-2 py-1 bg-white rounded border border-[#2C5F44]/20">⌘K</kbd> to search</span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
